import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatFCFA, imageUrl, whatsappLink, timeAgo } from "../lib/format";
import { useCart } from "../context/CartContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import ProductCard from "../components/storefront/ProductCard";
import { toast } from "sonner";
import { Dialog, DialogContent } from "../components/ui/dialog";
import { Sparkles, MessageCircle, Eye, Truck, Share2 } from "lucide-react";
import { TrustBadges } from "../components/storefront/TrustBadges";
import { HeartButton } from "../components/storefront/HeartButton";
import { NotifyMe } from "../components/storefront/NotifyMe";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [reco, setReco] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const { addItem, items: cartItems } = useCart();
  const { settings } = useSettings();
  const [viewers, setViewers] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveImg(0);
    });
    api.get(`/products/${id}/similaires`).then((r) => setSimilar(r.data));
    setReco(null);
    api.get(`/products/${id}/recommandations-ia`).then((r) => setReco(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    let vid = sessionStorage.getItem("viewer_id");
    if (!vid) {
      vid = (window.crypto && window.crypto.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));
      sessionStorage.setItem("viewer_id", vid);
    }
    let active = true;
    const ping = async () => {
      if (document.hidden) return;
      try {
        const r = await api.post(`/products/${id}/presence`, { viewer_id: vid });
        if (!active) return;
        setViewers(r.data.viewers || 0);
        if (r.data.disponibilite === "Rupture") {
          setProduct((prev) => {
            if (prev && prev.disponibilite !== "Rupture") {
              toast.error("Cette pièce vient d'être vendue.");
              return { ...prev, disponibilite: "Rupture", vendu_at: r.data.vendu_at };
            }
            return prev;
          });
        }
      } catch (e) {}
    };
    ping();
    const t = setInterval(ping, 15000);
    return () => { active = false; clearInterval(t); };
  }, [id]);

  if (!product) return <div className="py-32 text-center text-[#9B7E73]">Chargement...</div>;

  const images = product.images && product.images.length ? product.images : [null];
  const disponible = product.disponibilite !== "Rupture";
  const waNumber = settings?.whatsapp_number || "+225 01 02 84 94 17";
  const waMessage = disponible
    ? `Bonjour Le Dressing de Yoyo 💛\nJe souhaite réserver cette pièce :\n\n${product.nom}${product.taille ? `\nTaille : ${product.taille}` : ""}\nPrix : ${formatFCFA(product.prix)}\n\n${window.location.href}`
    : `Bonjour Le Dressing de Yoyo 💛\nLa pièce « ${product.nom} » est vendue. Auriez-vous une pièce similaire disponible ?\n\n${window.location.href}`;

  const handleAdd = () => {
    if (!disponible) {
      toast.error("Cette pièce n'est plus disponible.");
      return;
    }
    addItem(product, product.taille || "", 1);
    toast.success(`${product.nom} ajouté au panier`);
  };

  const shareUrl = `${BACKEND_URL}/api/share/${product.id}?src=partage`;
  const waShareUrl = `${BACKEND_URL}/api/share/${product.id}?src=whatsapp`;
  const shareText = `Regarde cette pièce unique chez Le Dressing de Yoyo : ${product.nom} — ${formatFCFA(product.prix)}`;
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: product.nom, text: shareText, url: shareUrl }); } catch (e) {}
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Lien copié — collez-le dans votre story ou message Instagram");
    } catch (e) {
      toast.error("Impossible de copier le lien");
    }
  };

  const addLook = () => {
    const inCart = new Set(cartItems.map((i) => i.product_id));
    const pieces = [product, ...(reco?.produits || [])].filter((p) => p.disponibilite !== "Rupture" && !inCart.has(p.id));
    if (!pieces.length) { toast.info("Toutes les pièces disponibles sont déjà dans votre panier."); return; }
    pieces.forEach((p) => addItem(p, p.taille || "", 1));
    toast.success(`${pieces.length} pièce(s) du look ajoutée(s) au panier`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <Link to="/catalog" className="mb-6 inline-block text-sm text-[#9B7E73] hover:text-[#D76E4D]">← Retour au Dressing</Link>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
        {/* Gallery */}
        <div>
          <div
            className="cursor-zoom-in overflow-hidden rounded-2xl bg-[#F5E8E3]"
            onClick={() => setZoom(true)}
            data-testid="product-main-image"
          >
            <img src={imageUrl(images[activeImg])} alt={product.nom} className="block h-auto w-full" />
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((img, i) => (
                <button
                  key={img || i}
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`h-20 w-16 overflow-hidden rounded-lg ring-2 ${activeImg === i ? "ring-[#D76E4D]" : "ring-transparent"}`}
                >
                  <img src={imageUrl(img, 320)} alt="" loading="lazy" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Meta */}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-[#F6E4DE] px-3 py-1 text-xs font-medium text-[#C05A3A]">{product.etat_client}</span>
            {!disponible && (
              <span data-testid="rupture-badge" className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-red-600">Rupture de stock</span>
            )}
          </div>
          <div className="mt-3 flex items-start justify-between gap-3">
            <h1 className="font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">{product.nom}</h1>
            <HeartButton product={product} className="shrink-0 ring-1 ring-[#E8D5CE]" />
          </div>
          <p className="mt-3 text-2xl font-semibold text-[#D76E4D]">{formatFCFA(product.prix)}</p>
          <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#1da851]" data-testid="free-delivery-note">
            <Truck className="h-4 w-4" /> Livraison gratuite à Abidjan
          </p>
          <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-[#6E544A]">{product.description}</p>

          {product.taille && (
            <div className="mt-6" data-testid="product-taille">
              <span className="mr-2 text-sm font-medium text-[#2C1A14]">Taille :</span>
              <span className="inline-block rounded-full bg-[#2C1A14] px-4 py-1.5 text-sm font-semibold uppercase tracking-wider text-white">{product.taille}</span>
            </div>
          )}
          {/* Rareté & urgence */}
          <div className="mt-4 space-y-2">
            <p className="inline-flex items-center gap-1.5 rounded-full bg-[#2C1A14] px-3 py-1 text-xs font-semibold text-white" data-testid="unique-badge">
              <Sparkles className="h-3.5 w-3.5" /> Pièce unique — 1 seul exemplaire
            </p>
            {disponible && viewers >= 2 && (
              <p className="flex items-center gap-1.5 text-sm font-medium text-[#D76E4D]" data-testid="viewers-live">
                <Eye className="h-4 w-4" /> {viewers} personnes regardent cette pièce en ce moment
              </p>
            )}
            {!disponible && product.vendu_at && (
              <p className="text-sm font-medium text-red-600" data-testid="sold-ago">Vendu {timeAgo(product.vendu_at)}</p>
            )}
          </div>

          {product.conseils_yoyo && (
            <div className="mt-6 rounded-xl bg-[#F6E4DE] p-4" data-testid="conseils-yoyo">
              <p className="font-script text-2xl text-[#D76E4D]">Le conseil de Yoyo</p>
              <p className="mt-1 text-sm leading-relaxed text-[#6E544A]">{product.conseils_yoyo}</p>
            </div>
          )}

          {disponible ? (
            <Button
              onClick={handleAdd}
              data-testid="add-to-cart-button"
              className="mt-8 w-full rounded-full bg-[#D76E4D] py-6 text-base text-white hover:bg-[#C05A3A]"
            >
              Ajouter au panier
            </Button>
          ) : (
            <div
              data-testid="rupture-cta"
              className="mt-8 w-full cursor-not-allowed rounded-full bg-gray-200 py-4 text-center text-base font-medium text-gray-500"
            >
              Rupture de stock — pièce vendue
            </div>
          )}
          {!disponible && <NotifyMe productId={product.id} />}

          <a
            href={whatsappLink(waNumber, waMessage)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-order-button"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-4 text-base font-medium text-white transition-transform hover:scale-[1.02] active:scale-95"
          >
            <MessageCircle className="h-5 w-5" />
            {disponible ? "Commander sur WhatsApp" : "Demander une pièce similaire"}
          </a>

          <div className="mt-4 flex items-center gap-2">
            <span className="text-sm text-[#9B7E73]">Partager :</span>
            <a href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${waShareUrl}`)}`} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp"
              className="flex items-center gap-1.5 rounded-full border border-[#E8D5CE] bg-white px-3 py-1.5 text-sm text-[#2C1A14] transition-colors hover:border-[#25D366] hover:text-[#1da851]">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <button type="button" onClick={shareNative} data-testid="share-instagram"
              className="flex items-center gap-1.5 rounded-full border border-[#E8D5CE] bg-white px-3 py-1.5 text-sm text-[#2C1A14] transition-colors hover:border-[#D76E4D] hover:text-[#D76E4D]">
              <Share2 className="h-4 w-4" /> Instagram / autre
            </button>
          </div>

          <TrustBadges className="mt-6 rounded-xl bg-[#F6E4DE] p-4" />
        </div>
      </div>

      {reco && reco.produits && reco.produits.length > 0 && (
        <section className="mt-16 rounded-2xl bg-[#F5E8E3] p-6 sm:p-8" data-testid="reco-ia-section">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-[#D76E4D]" />
              <h2 className="font-serif text-2xl font-medium text-[#2C1A14]">Complétez le look</h2>
            </div>
            <Button onClick={addLook} data-testid="add-look-to-cart" className="rounded-full bg-[#2C1A14] text-white hover:bg-[#D76E4D]">
              Ajouter tout le look au panier
            </Button>
          </div>
          {reco.analyse && <p className="mb-6 max-w-2xl text-base leading-relaxed text-[#6E544A]">{reco.analyse}</p>}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
            {reco.produits.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {similar.length > 0 && (
        <section className="mt-16">
          <h2 className="mb-6 font-serif text-2xl font-medium text-[#2C1A14]">Vous aimerez aussi</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
            {similar.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="max-w-3xl border-none bg-transparent p-0 shadow-none">
          <img src={imageUrl(images[activeImg])} alt={product.nom} className="max-h-[85vh] w-full rounded-xl object-contain" />
        </DialogContent>
      </Dialog>
    </div>
  );
}
